import * as React from 'react';

import { cn } from '../../../lib/utils';

// ─── Types ────────────────────────────────────────────────────────────────────

export interface IconTextProps {
  /** Content to display after the icon. Can be a plain string or a React node (e.g. StackedCell). */
  children: React.ReactNode;
  /** Additional classes applied to the outer flex wrapper (e.g. `items-start`, `mt-1`, colour tokens). */
  className?: string;
  /** The icon component to render — any Lucide icon or compatible ElementType. */
  icon: React.ElementType;
  /** Additional classes applied to the icon element (appended to the default size + colour classes). */
  iconClassName?: string;
  /** Inline style applied to the icon element, for dynamic colours (e.g. site-type colours). */
  iconStyle?: React.CSSProperties;
  /** Size variant — controls icon dimensions and inherited text size. */
  size?: 'sm' | 'xs';
}

// ─── Constants ────────────────────────────────────────────────────────────────

const ICON_SIZE: Record<NonNullable<IconTextProps['size']>, string> = {
  sm: 'w-3.5 h-3.5',
  xs: 'w-3 h-3',
};

const TEXT_SIZE: Record<NonNullable<IconTextProps['size']>, string> = {
  sm: 'text-sm',
  xs: 'text-xs',
};

// ─── Component ────────────────────────────────────────────────────────────────

/**
 * Renders a small muted leading icon followed by arbitrary content in a flex row.
 *
 * Replaces the repeated `flex items-center gap-1.5 <icon + text>` pattern used
 * across date-slot-cell, location-cell, pickup-delivery-section, and time-slot-field.
 *
 * Size variants:
 *   - `sm` (default) — icon w-3.5 h-3.5, wrapper text-sm
 *   - `xs` — icon w-3 h-3, wrapper text-xs
 *
 * The icon receives `text-muted-foreground shrink-0` by default. Override via
 * `iconClassName` (merged via cn/tailwind-merge) or supply `iconStyle` for
 * dynamic CSS colour values (e.g. site-type brand colours).
 *
 * Use `className` to override wrapper alignment (`items-start` for multi-line
 * children), add spacing (`mt-1`), or inject colour inheritance tokens.
 */
export function IconText({
  children,
  className,
  icon: Icon,
  iconClassName,
  iconStyle,
  size = 'sm',
}: IconTextProps): React.JSX.Element {
  return (
    <div className={cn('flex items-center gap-1.5', TEXT_SIZE[size], className)}>
      <Icon className={cn(ICON_SIZE[size], 'shrink-0 text-muted-foreground', iconClassName)} style={iconStyle} />
      {children}
    </div>
  );
}
